import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import isBetween from 'dayjs/plugin/isBetween'
import {
  Note,
  NoteType,
  isCalendarNote,
  filenameToDate,
  Timeframe,
  isTeamspaceNote,
} from '../../utils/note'
import { parseTaskState } from '../../utils/task'

// Extend dayjs with required plugins
dayjs.extend(quarterOfYear)
dayjs.extend(isBetween)

// Task limit constant - used to prevent performance issues
export const TASK_LIMIT = 500
import {
  DateRange,
  FilterConfig,
  Level,
  TaskState,
  ChecklistState,
  TaskItem,
  TaskGroup,
} from './types'
import { getNoteFromAnyCache, type CacheData } from '../../contexts/CachedNotes'
import { QueryClient } from '@tanstack/react-query'

import { cacheKeys } from '../../utils/queryKeyFactory'
import { getTeamSpace } from '../../utils/teamSpace'

// Types for sorting
type DateSource = 'task' | 'calendar' | 'modified'
type DateConfidence = 'high' | 'medium' | 'low'

type SortableDate = {
  primaryDate: dayjs.Dayjs
  dateSource: DateSource
  confidence: DateConfidence
}

// Cache for expensive date extractions
const dateCache = new WeakMap<Note, SortableDate>()

/**
 * Extract scheduled date from task content
 *
 * @param content - Task content to parse
 * @returns Scheduled date string or undefined
 */
export function extractScheduledDate(content: string): string | undefined {
  // Extract scheduled date if present (>YYYY-MM-DD or >today format)
  // Exclude matches when preceded by backticks (e.g., `>today`)
  const dateMatch = content.match(/(?<=^|[^\\`])>(\d{4}-\d{2}-\d{2})/)
  const todayMatch = content.match(/(?<=^|[^\\`])>today\b/)

  return dateMatch ? dateMatch[1] : todayMatch ? 'today' : undefined
}

// Helper function to determine effective task state considering scheduled dates and context
function getEffectiveTaskState(
  state: string,
  content: string,
  context?: string,
  noteType?: NoteType[]
): {
  effectiveState: 'done' | 'cancelled' | 'scheduled' | 'open'
  scheduledDate: string | undefined
} {
  const scheduledDate = extractScheduledDate(content)

  let effectiveState: 'done' | 'cancelled' | 'scheduled' | 'open'
  if (state === 'x') {
    effectiveState = 'done'
  } else if (state === '-') {
    effectiveState = 'cancelled'
  } else if (state === '>') {
    // Always scheduled if explicitly marked with '>' state
    effectiveState = 'scheduled'
  } else if (scheduledDate) {
    // Has scheduled date but no explicit '>' state
    // If we are filtering by project notes, we always show as scheduled
    if(noteType?.includes(NoteType.PROJECT_NOTE) ?? noteType?.includes(NoteType.ASSET_PROJECT_NOTE) ?? noteType?.includes(NoteType.TEAM_SPACE_NOTE)) {
      effectiveState = 'scheduled'
    } else if (context) {
      // If the note is not a project note, we check if the scheduled date is the same as the context = the group we are showing this task in
      const targetDate = scheduledDate === 'today' ? dayjs().format('YYYY-MM-DD') : scheduledDate
      
      effectiveState = 
        // The group we are showing this task in is the same as the scheduled date - show as scheduled
        targetDate === context ? 'scheduled' : 
        // We're showing the task on the target date which is not the same as the context (group we are showing it in) - show as open
        'open'
    } else {
      // No context provided - default to scheduled for filtering purposes
      effectiveState = 'scheduled'
    }
  } else {
    effectiveState = 'open'
  }

  return { effectiveState, scheduledDate }
}

// Check if a note matches the specified note type filter
export function matchesNoteType(note: Note, noteTypes?: NoteType[]): boolean {
  if (!noteTypes || noteTypes.length === 0) return true
  return noteTypes.includes(note.noteType)
}

// Check if a note matches the specified task or checklist state filters
export function matchesTaskState(
  note: Note,
  taskStates?: TaskState[],
  checklistStates?: ChecklistState[]
): boolean {
  // If no filters specified, show all
  if (
    (!taskStates || taskStates.length === 0) &&
    (!checklistStates || checklistStates.length === 0)
  ) {
    return true
  }

  if (!note.content) return false

  // Parse the note content to find tasks and checklists
  const lines = note.content.split('\n')

  // Check if any line in the note matches the task/checklist state filters
  const hasMatchingItem = lines.some((line) => {
    const parsed = parseTaskState(line)
    if (!parsed) return false

    const { isTask, isChecklist, state, content } = parsed

    // Get effective task state using shared helper
    const { effectiveState } = getEffectiveTaskState(state, content)

    // If we have task filters active, only check tasks
    if (taskStates && taskStates.length > 0) {
      if (!isTask) return false // Skip checklists when task filter is active
      const taskState = `task-${effectiveState}` as TaskState
      return taskStates.includes(taskState)
    }

    // If we have checklist filters active, only check checklists
    if (checklistStates && checklistStates.length > 0) {
      if (!isChecklist) return false // Skip tasks when checklist filter is active
      const checklistState = `checklist-${effectiveState}` as ChecklistState
      return checklistStates.includes(checklistState)
    }

    return false
  })

  return hasMatchingItem
}

// Check if a note matches the specified date range filter
export function matchesDateRange(note: Note, dateRange?: DateRange): boolean {
  if (!dateRange) return true

  // Date range filters only apply to calendar notes
  // Regular/project notes should always pass the date filter
  if (!isCalendarNote(note.noteType)) {
    return true
  }

  // For calendar notes, check the date from the filename
  const { date: noteDate, timeframe } = filenameToDate(note.filename)
  if (noteDate) {
    return isDateInRange(noteDate, dateRange, timeframe)
  }

  // If we can't extract a date from the calendar note filename, exclude it
  return false
}

// Helper function to check if a date is within a specified range
export function isDateInRange(
  date: dayjs.Dayjs,
  dateRange: DateRange,
  timeframe?: Timeframe | null
): boolean {
  const today = dayjs()

  switch (dateRange.type) {
    case 'thisMonth': {
      // For different periods, check if they overlap with current month
      switch (timeframe) {
        case 'week': {
          // Week overlaps current month if it starts or ends in current month
          const weekStart = date.startOf('week')
          const weekEnd = date.endOf('week')
          return (
            (weekStart.month() === today.month() &&
              weekStart.year() === today.year()) ||
            (weekEnd.month() === today.month() &&
              weekEnd.year() === today.year())
          )
        }
        case 'quarter': {
          // Quarter contains current month
          const quarterStart = date.startOf('quarter')
          const quarterEnd = date.endOf('quarter')
          const currentMonth = today.startOf('month')
          // eslint-disable-next-line unicorn/no-null -- dayjs uses null
          return currentMonth.isBetween(quarterStart, quarterEnd, null, '[]')
        }
        case 'year': {
          // Year contains current month
          return date.year() === today.year()
        }
        // No default
      }
      // Default: exact month match for day and month periods
      return date.month() === today.month() && date.year() === today.year()
    }

    case 'thisWeek': {
      // Check if date is in current week, accounting for different periods
      const currentWeekStart = today.startOf('week')
      const currentWeekEnd = today.endOf('week')

      switch (timeframe) {
        case 'month': {
          // Month overlaps current week if any part is in current week
          const monthStart = date.startOf('month')
          const monthEnd = date.endOf('month')
          return (
            monthStart.isBefore(currentWeekEnd.add(1, 'day')) &&
            monthEnd.isAfter(currentWeekStart.subtract(1, 'day'))
          )
        }
        case 'quarter': {
          // Quarter overlaps current week
          const quarterStart = date.startOf('quarter')
          const quarterEnd = date.endOf('quarter')
          return (
            quarterStart.isBefore(currentWeekEnd.add(1, 'day')) &&
            quarterEnd.isAfter(currentWeekStart.subtract(1, 'day'))
          )
        }
        case 'year': {
          // Year contains current week
          return date.year() === today.year()
        }
        // No default
      }
      // Default: date is within current week
      // eslint-disable-next-line unicorn/no-null -- dayjs uses null
      return date.isBetween(currentWeekStart, currentWeekEnd, null, '[]')
    }

    case 'thisQuarter': {
      const currentQuarterStart = today.startOf('quarter')
      const currentQuarterEnd = today.endOf('quarter')

      if (timeframe === 'week') {
        // Week overlaps current quarter
        const weekStart = date.startOf('week')
        const weekEnd = date.endOf('week')
        return (
          weekStart.isBefore(currentQuarterEnd.add(1, 'day')) &&
          weekEnd.isAfter(currentQuarterStart.subtract(1, 'day'))
        )
      } else if (timeframe === 'year') {
        // Year contains current quarter
        return date.year() === today.year()
      }
      // Default: date is within current quarter
      // eslint-disable-next-line unicorn/no-null -- dayjs uses null
      return date.isBetween(currentQuarterStart, currentQuarterEnd, null, '[]')
    }

    case 'thisYear': {
      // All periods: just check if year matches
      return date.year() === today.year()
    }

    case 'rolling30Days': {
      const startDate = today.subtract(1, 'day').startOf('day') // Include yesterday
      const endDate = today.add(29, 'day').endOf('day') // 31 total days (yesterday + today + 29 future)
      // eslint-disable-next-line unicorn/no-null -- dayjs uses null
      return date.isBetween(startDate, endDate, null, '[]') // [] means inclusive on both ends
    }

    case 'future': {
      return date.isAfter(today)
    }

    case 'past': {
      return date.isBefore(today)
    }

    case 'custom': {
      return (
        date.isAfter(dayjs(dateRange.start)) &&
        date.isBefore(dayjs(dateRange.end))
      )
    }

    default: {
      return false
    }
  }
}

// Check if a note matches the specified folder filter (including subfolders)
export function matchesFolder(
  note: Note,
  folders?: string[],
  cachedNotesQueryClient?: QueryClient,
  userId?: string,
  teamUserId?: string
): boolean {
  if (!folders || folders.length === 0) return true

  // If no cache or user, try direct parent ID matching
  if (!cachedNotesQueryClient || !userId) {
    return note.parent ? folders.includes(note.parent) : false
  }

  // Get folder paths for regular folder matching
  const selectedFolderPaths: string[] = []
  for (const folderId of folders) {
    const folderNote =
      getNoteFromAnyCache(cachedNotesQueryClient, userId, folderId) ??
      getNoteFromAnyCache(cachedNotesQueryClient, teamUserId ?? '', folderId)
    if (folderNote?.filename) {
      selectedFolderPaths.push(folderNote.filename)
    } else if (folderNote?.title) {
      selectedFolderPaths.push(folderNote.title)
    }
  }

  // Extract note's folder path from filename
  let noteFolderPath: string | undefined
  if (note.filename.includes('/')) {
    const pathParts = note.filename.split('/')
    if (pathParts.length > 1) {
      pathParts.pop() // Remove filename, keep folder path
      noteFolderPath = pathParts.join('/')
    }
  }

  // Walk up parent chain for Supabase notes (fallback)
  if (!noteFolderPath && note.parent) {
    let currentNote = note
    const visited = new Set<string>()
    const pathParts: string[] = []

    while (currentNote.parent && !visited.has(currentNote.parent)) {
      visited.add(currentNote.parent)

      // Direct parent ID match for teamspace notes
      if (folders.includes(currentNote.parent)) {
        return true
      }

      // Use teamUserId for teamspace notes, otherwise use userId
      const userIdToUse = isTeamspaceNote(currentNote.noteType)
        ? teamUserId ?? userId // Fallback to userId if teamUserId is undefined
        : userId
      const parentNote = getNoteFromAnyCache(
        cachedNotesQueryClient,
        userIdToUse,
        currentNote.parent
      )
      if (!parentNote) break

      const pathPart = parentNote.filename
      if (pathPart) {
        pathParts.unshift(pathPart)
      }
      currentNote = parentNote
    }

    if (pathParts.length > 0) {
      noteFolderPath = pathParts.join('/')
    }
  }

  if (!noteFolderPath) return false

  // Check if note's folder matches any selected folder (including subfolders)
  return selectedFolderPaths.some(
    (selectedPath) =>
      noteFolderPath === selectedPath ||
      noteFolderPath.startsWith(selectedPath + '/')
  )
}

// Check if a note matches the search term
export function matchesSearchTerm(note: Note, searchTerm?: string): boolean {
  if (!searchTerm || searchTerm.trim() === '') return true

  const term = searchTerm.toLowerCase().trim()

  // Check title
  if (note.title?.toLowerCase().includes(term)) return true

  // Check content
  if (note.content?.toLowerCase().includes(term)) return true

  return false
}

// Check if a note matches the level filter
export function matchesLevel(note: Note, level?: Level): boolean {
  if (!level) return true

  switch (level) {
    case Level.TASKS: {
      // Check if the note has any task/checklist markers
      if (!note.content) return false
      const lines = note.content.split('\n')
      return lines.some((line) => /^\s*[*+]\s+.+$/.test(line))
    }

    case Level.NOTES: {
      // Placeholder for notes level - will be implemented later
      // For now, include all notes
      return true
    }

    case Level.EVENTS: {
      // Placeholder for events level - will be implemented later
      // For now, exclude all notes (events not implemented)
      return false
    }

    default: {
      return true
    }
  }
}

// Period normalization - always use first day of period
function normalizePeriodToSortableDate(dateString: string): dayjs.Dayjs {
  if (dateString === 'today') return dayjs()

  // Weekly: Use Monday (start of week)
  if (dateString.includes('-W')) {
    const [year, week] = dateString.split('-W')
    return dayjs()
      .year(Number.parseInt(year))
      .week(Number.parseInt(week))
      .startOf('week')
  }

  // Quarterly: Use start of quarter
  if (dateString.includes('-Q')) {
    const [year, quarter] = dateString.split('-Q')
    return dayjs()
      .year(Number.parseInt(year))
      .quarter(Number.parseInt(quarter))
      .startOf('quarter')
  }

  // Monthly: Use 1st of month
  if (/^\d{4}-\d{2}$/.test(dateString)) {
    return dayjs(dateString + '-01')
  }

  // Yearly: Use January 1st
  if (/^\d{4}$/.test(dateString)) {
    return dayjs(dateString + '-01-01')
  }

  // Daily: Use as-is
  return dayjs(dateString)
}

// Extract earliest task date from note content
function extractEarliestTaskDate(content?: string): dayjs.Dayjs | undefined {
  if (!content) return

  const dates: dayjs.Dayjs[] = []
  // Exclude matches when preceded by backticks (e.g., `>today`)
  const dateRegex =
    /(?<=^|[^\\`])>(?:today|\d{4}(?:(?:-\d{2}){1,2}|-W\d{1,2}|-Q[1-4])?)/g

  let match
  // eslint-disable-next-line unicorn/no-null -- regex exec returns null
  while ((match = dateRegex.exec(content)) != null) {
    const dateString = match[0].slice(1) // Remove '>' prefix
    const normalizedDate = normalizePeriodToSortableDate(dateString)
    if (normalizedDate.isValid()) {
      dates.push(normalizedDate)
    }
  }

  // Return earliest date for urgency-based sorting
  if (dates.length === 0) return undefined

  let earliest = dates[0]
  for (let index = 1; index < dates.length; index++) {
    if (dates[index].isBefore(earliest)) {
      earliest = dates[index]
    }
  }
  return earliest
}

// Main date extraction function with priority chain
export function extractSortableDate(note: Note): SortableDate {
  // Check cache first
  const cache = dateCache.get(note)
  if (cache) return cache

  let result: SortableDate

  // Priority 1: Task/checklist scheduled dates
  const taskDate = extractEarliestTaskDate(note.content)
  if (taskDate) {
    result = {
      primaryDate: taskDate,
      dateSource: 'task',
      confidence: 'high',
    }
  }
  // Priority 2: Calendar note period date
  else if (isCalendarNote(note.noteType)) {
    const { date } = filenameToDate(note.filename)
    if (date) {
      result = {
        primaryDate: date,
        dateSource: 'calendar',
        confidence: 'high',
      }
    } else {
      // Fallback to modified date for invalid calendar notes
      const modifiedDate = note.fileModifiedAt
        ? dayjs(note.fileModifiedAt)
        : dayjs()
      result = {
        primaryDate: modifiedDate,
        dateSource: 'modified',
        confidence: 'low',
      }
    }
  }
  // Priority 3: File modification date
  else {
    const modifiedDate = note.fileModifiedAt
      ? dayjs(note.fileModifiedAt)
      : dayjs()
    result = {
      primaryDate: modifiedDate,
      dateSource: 'modified',
      confidence: 'low',
    }
  }

  // Cache the result
  dateCache.set(note, result)
  return result
}

// Sort notes by date with calendar notes prioritized first
export function sortNotesByDate(
  notes: Note[],
  direction: 'asc' | 'desc' = 'asc'
): Note[] {
  return notes.sort((a, b) => {
    const aIsCalendar = isCalendarNote(a.noteType)
    const bIsCalendar = isCalendarNote(b.noteType)

    // Primary sort: calendar notes first, then project notes
    if (aIsCalendar !== bIsCalendar) {
      return aIsCalendar ? -1 : 1
    }

    // Secondary sort: by date within each category
    const dateA = extractSortableDate(a)
    const dateB = extractSortableDate(b)
    const dateDiff = dateA.primaryDate.diff(dateB.primaryDate)
    if (dateDiff !== 0) {
      return direction === 'asc' ? dateDiff : -dateDiff
    }

    // Tertiary sort: by confidence (high confidence first)
    const confidenceOrder = { high: 3, medium: 2, low: 1 }
    const confidenceDiff =
      confidenceOrder[dateB.confidence] - confidenceOrder[dateA.confidence]
    if (confidenceDiff !== 0) {
      return confidenceDiff
    }

    // Quaternary sort: by date source priority (task > calendar > modified)
    const sourceOrder = { task: 3, calendar: 2, modified: 1 }
    const sourceDiff =
      sourceOrder[dateB.dateSource] - sourceOrder[dateA.dateSource]
    if (sourceDiff !== 0) {
      return sourceDiff
    }

    // Final fallback: alphabetical by filename
    return a.filename.localeCompare(b.filename)
  })
}

// Clear date cache (useful for testing or memory management)
export function clearDateCache() {
  // WeakMap doesn't have a clear method, but this function can be extended
  // if we switch to Map with manual cleanup
}

// Unified task extraction function
export function extractTasksFromNote(
  note: Note,
  filterConfig?: FilterConfig
): TaskItem[] {
  if (!note.content) return []

  const tasks: TaskItem[] = []
  const lines = note.content.split('\n')

  for (const [lineIndex, line] of lines.entries()) {
    const parsed = parseTaskState(line)
    if (!parsed) continue

    const { isTask, isChecklist, bullet, content, state } = parsed

    // Get effective task state and scheduled date using shared helper
    const { effectiveState, scheduledDate } = getEffectiveTaskState(
      state,
      content,
      note.title,
      filterConfig?.noteType
    )

    // Extract tag if present (#tag format)
    const tagMatch = content.match(/#(\w+)/)
    const tag = tagMatch ? tagMatch[1] : undefined

    // Apply task/checklist state filters
    if (filterConfig?.taskState && filterConfig.taskState.length > 0) {
      if (!isTask) continue

      let taskState: TaskState
      switch (effectiveState) {
        case 'done': {
          taskState = TaskState.DONE
          break
        }
        case 'cancelled': {
          taskState = TaskState.CANCELLED
          break
        }
        case 'scheduled': {
          taskState = TaskState.SCHEDULED
          break
        }
        default: {
          taskState = TaskState.OPEN
        }
      }

      if (!filterConfig.taskState.includes(taskState)) {
        continue
      }
    }

    // Filter based on checklist state if specified
    if (
      filterConfig?.checklistState &&
      filterConfig.checklistState.length > 0
    ) {
      if (!isChecklist) continue

      let checklistState: ChecklistState
      switch (state) {
        case 'x': {
          checklistState = ChecklistState.DONE

          break
        }
        case '-': {
          checklistState = ChecklistState.CANCELLED

          break
        }
        case '>': {
          checklistState = ChecklistState.SCHEDULED

          break
        }
        default: {
          checklistState = ChecklistState.OPEN
        }
      }

      if (!filterConfig.checklistState.includes(checklistState)) {
        continue
      }
    }

    const taskItem: TaskItem = {
      content,
      state,
      bullet,
      scheduledDate,
      tag,
      lineIndex,
      sourceNote: note,
      originalLine: line, // Store the original line content for accurate task finding
    }

    // Apply date range filter if specified
    if (filterConfig?.dateRange) {
      // Determine the task's effective date for filtering
      let taskDate: dayjs.Dayjs | undefined
      if (scheduledDate) {
        // Use explicitly scheduled date
        taskDate = scheduledDate === 'today' ? dayjs() : dayjs(scheduledDate)

      } else if (isCalendarNote(note.noteType)) {
        // Use calendar note's date
        const { date } = filenameToDate(note.filename)
        taskDate = date ?? dayjs()
      } 

      // Skip task if it doesn't match the date range
      if (!taskDate || !isDateInRange(taskDate, filterConfig.dateRange)) {
        continue
      }
    }

    // Apply search term filter to individual task content if specified
    if (filterConfig?.searchTerm && filterConfig.searchTerm.trim() !== '') {
      const searchTerm = filterConfig.searchTerm.toLowerCase().trim()

      // Check if task content contains the search term
      const taskMatches = content.toLowerCase().includes(searchTerm)

      // Also check note title for context (e.g., searching for note name)
      const noteTitleMatches =
        note.title?.toLowerCase().includes(searchTerm) ?? false

      // Skip task if neither task content nor note title matches search term
      if (!taskMatches && !noteTitleMatches) {
        continue
      }
    }

    tasks.push(taskItem)
  }

  return tasks
}

// Extract tasks from notes and group by date for calendar notes
export function extractAndGroupTasksByDate(
  notes: Note[],
  filterConfig?: FilterConfig,
  sortDirection: 'asc' | 'desc' = 'asc',
  limit = TASK_LIMIT
): { taskGroups: TaskGroup[], limitReached: boolean, totalTaskCount: number } {
  const tasksByDate = new Map<string, TaskItem[]>()
  let totalTasksCollected = 0
  let limitReached = false

  for (const note of notes) {
    // Stop processing more notes if we've collected enough tasks
    if (totalTasksCollected >= limit) {
      limitReached = true
      break
    }
    
    const tasks = extractTasksFromNote(note, filterConfig)
    const isCalendar = isCalendarNote(note.noteType)

    // Get the note's date for calendar notes
    let noteDate: dayjs.Dayjs | undefined
    let noteTitle: string | undefined = undefined

    if (isCalendar) {
      const { date } = filenameToDate(note.filename)
      noteDate = date
      noteTitle = note.title
    }

    for (const task of tasks) {
      // Determine the date to group by
      let groupDate: string

      // Only use scheduled date for open tasks (not completed/cancelled)
      // Completed tasks should stay on the date they were completed (note's date)
      if (task.scheduledDate && task.state !== 'x' && task.state !== '-') {
        // Use explicitly scheduled date for open/scheduled tasks
        groupDate =
          task.scheduledDate === 'today'
            ? dayjs().format('YYYY-MM-DD')
            : task.scheduledDate
      } else if (noteDate) {
        // Use calendar note's date for completed tasks or tasks without scheduled date
        groupDate = noteTitle ?? noteDate.format('YYYY-MM-DD')
      } else {
        // For non-calendar notes without scheduled date, use today
        groupDate = dayjs().format('YYYY-MM-DD')
      }

      // If we have a date range filter, only include tasks that match the date range
      if (filterConfig?.dateRange) {
        const taskDate = filenameToDate(note.filename).date
        if (taskDate && !isDateInRange(taskDate, filterConfig.dateRange)) {
          continue // Skip this task if it doesn't match the date range
        }
      }

      // Stop collecting if we've reached the limit
      if (totalTasksCollected >= limit) {
        limitReached = true
        break
      }

      // Add to grouped tasks
      if (!tasksByDate.has(groupDate)) {
        tasksByDate.set(groupDate, [])
      }
      tasksByDate.get(groupDate)?.push(task)
      totalTasksCollected++
    }
  }

  // Convert to TaskGroup array and sort by date
  const taskGroups: TaskGroup[] = [...tasksByDate.entries()]
    .map(([date, tasks]) => ({
      date,
      displayDate: formatDateForDisplay(date),
      tasks: tasks.sort((a, b) => {
        // We want to sort up the referenced tasks (i.e. scheduled tasks)
        // Convert source note filenames to dates for comparison
        const aSourceDate = filenameToDate(a.sourceNote.filename).date?.format(
          'YYYY-MM-DD'
        )
        const bSourceDate = filenameToDate(b.sourceNote.filename).date?.format(
          'YYYY-MM-DD'
        )

        // Check if source dates match the current group date
        const aIsFromSameDate = aSourceDate === date
        const bIsFromSameDate = bSourceDate === date

        // If one is from the same date and other is not, prioritize the one NOT from same date
        if (aIsFromSameDate !== bIsFromSameDate) {
          return aIsFromSameDate ? 1 : -1 // Same date goes down (1), different date goes up (-1)
        }

        // If both have same priority, sort by line index
        return a.lineIndex - b.lineIndex
      }), // Maintain order within note, but prioritize referenced tasks
      actualDate: filenameToDate(date).date ?? dayjs()
    }))
    .sort((a, b) => {
      // Use actualDate for proper chronological sorting
      const diff = a.actualDate.diff(b.actualDate)
      return sortDirection === 'desc' ? -diff : diff
    }) // Sort groups by date with configurable direction

  return { taskGroups, limitReached, totalTaskCount: totalTasksCollected }
}

// Unified helper function to format date for display - matches calendar note title format
export function formatDateForDisplay(dateString: string): string {
  try {
    // Check if it's already a formatted display title (e.g., "Week 32", "July 2025")
    if (!/^\d{4}/.test(dateString)) {
      return dateString
    }

    // Check for week format (YYYY-Www)
    const weekMatch = dateString.match(/^(\d{4})W(\d{1,2})$/)
    if (weekMatch) {
      const [, year, week] = weekMatch
      return `Week ${week}, ${year}`
    }

    // Check for month format (YYYY-MM)
    const monthMatch = dateString.match(/^(\d{4})(\d{2})$/)
    if (monthMatch) {
      const [, year, month] = monthMatch
      const date = dayjs(`${year}-${month}-01`)
      return date.format('MMMM YYYY')
    }

    // Check for quarter format (YYYY-[Q]n)
    const quarterMatch = dateString.match(/^(\d{4})\[Q](\d)$/)
    if (quarterMatch) {
      const [, year, quarter] = quarterMatch
      return `Q${quarter} ${year}`
    }

    // Check for year format (YYYY)
    const yearMatch = dateString.match(/^(\d{4})$/)
    if (yearMatch) {
      return yearMatch[0]
    }

    // Default: assume YYYY-MM-DD format
    const date = dayjs(dateString)
    const today = dayjs()
    const isCurrentYear = date.year() === today.year()
    const format = isCurrentYear ? 'ddd, D MMM' : 'ddd, D MMM YYYY'

    if (date.isSame(today, 'day')) {
      return `Today, ${date.format(format)}`
    } else if (date.isSame(today.add(1, 'day'), 'day')) {
      return `Tomorrow, ${date.format(format)}`
    } else if (date.isSame(today.subtract(1, 'day'), 'day')) {
      return `Yesterday, ${date.format(format)}`
    } else {
      return date.format(format)
    }
  } catch {
    return dateString
  }
}

// Apply all filters to a note
export function applyFilters(
  note: Note,
  filters: FilterConfig,
  cachedNotesQueryClient?: QueryClient,
  userId?: string,
  teamUserId?: string
): boolean {
  return (
    matchesNoteType(note, filters.noteType) &&
    matchesTaskState(note, filters.taskState, filters.checklistState) &&
    matchesDateRange(note, filters.dateRange) &&
    matchesFolder(
      note,
      filters.folder,
      cachedNotesQueryClient,
      userId,
      teamUserId
    ) &&
    matchesSearchTerm(note, filters.searchTerm) &&
    matchesLevel(note, filters.level)
  )
}
